"use client"

import * as React from "react"
import { useRouter } from "next/navigation"

import {
  type ClientAuthResult,
  login as loginRequest,
  logout as logoutRequest,
  signup as signupRequest,
} from "@/lib/api/client/auth"
import { switchCompany as switchCompanyRequest } from "@/lib/api/client/companies"
import { identifyAnalyticsUser, resetAnalytics, trackEvent } from "@/lib/analytics"
import { resetCableConsumer } from "@/lib/realtime/cable"
import {
  type AuthUser,
  type Company,
  type LoginPayload,
  type SignupPayload,
} from "@/lib/api/types"

interface AuthContextValue {
  user: AuthUser | null
  hasCompany: boolean
  companies: Company[]
  currentCompanyId: string | null
  signup: (payload: SignupPayload) => Promise<ClientAuthResult>
  login: (payload: LoginPayload) => Promise<ClientAuthResult>
  logout: () => Promise<void>
  // redirectTo: a screen every organization has (default: Inicio).
  switchCompany: (companyId: string, options?: { redirectTo?: string }) => Promise<void>
}

const AuthContext = React.createContext<AuthContextValue | null>(null)

interface AuthProviderProps {
  children: React.ReactNode
  initialUser?: AuthUser | null
  initialHasCompany?: boolean
  initialCompanies?: Company[]
  initialCurrentCompanyId?: string | null
}

export function AuthProvider({
  children,
  initialUser = null,
  initialHasCompany = false,
  initialCompanies = [],
  initialCurrentCompanyId = null,
}: AuthProviderProps) {
  const router = useRouter()
  const [user, setUser] = React.useState<AuthUser | null>(initialUser)
  const [hasCompany, setHasCompany] = React.useState<boolean>(initialHasCompany)
  const [companies, setCompanies] = React.useState<Company[]>(initialCompanies)
  const [currentCompanyId, setCurrentCompanyId] = React.useState<string | null>(
    initialCurrentCompanyId,
  )
  const identifiedUserId = React.useRef<string | null>(null)

  const identifyUser = React.useCallback((authUser: AuthUser) => {
    if (identifiedUserId.current && identifiedUserId.current !== authUser.id) {
      resetAnalytics()
    }

    identifyAnalyticsUser(authUser.id, {
      email: authUser.email,
      name: `${authUser.name} ${authUser.lastName}`.trim(),
    })
    identifiedUserId.current = authUser.id
  }, [])

  // Re-seed client state when the server-rendered props change (e.g. after
  // router.refresh() following company creation). Without this, useState
  // would keep its first-render value forever.
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [prevInitialUser, setPrevInitialUser] = React.useState(initialUser)
  if (prevInitialUser !== initialUser) {
    setPrevInitialUser(initialUser)
    setUser(initialUser)
  }
  const [prevInitialHasCompany, setPrevInitialHasCompany] = React.useState(initialHasCompany)
  if (prevInitialHasCompany !== initialHasCompany) {
    setPrevInitialHasCompany(initialHasCompany)
    setHasCompany(initialHasCompany)
  }
  const [prevInitialCompanies, setPrevInitialCompanies] = React.useState(initialCompanies)
  if (prevInitialCompanies !== initialCompanies) {
    setPrevInitialCompanies(initialCompanies)
    setCompanies(initialCompanies)
  }
  const [prevInitialCurrentCompanyId, setPrevInitialCurrentCompanyId] =
    React.useState(initialCurrentCompanyId)
  if (prevInitialCurrentCompanyId !== initialCurrentCompanyId) {
    setPrevInitialCurrentCompanyId(initialCurrentCompanyId)
    setCurrentCompanyId(initialCurrentCompanyId)
  }

  // The server can hydrate an already-authenticated session on a page refresh.
  // Login and signup identify synchronously below; the ref prevents duplicate calls.
  React.useEffect(() => {
    if (user && identifiedUserId.current !== user.id) {
      identifyUser(user)
    }
  }, [identifyUser, user])

  const signup = React.useCallback(
    async (payload: SignupPayload) => {
      const result = await signupRequest(payload)
      resetCableConsumer()
      identifyUser(result.user)
      trackEvent("user_signed_up")
      setUser(result.user)
      setHasCompany(result.hasCompany)
      setCompanies(result.companies)
      router.refresh()
      return result
    },
    [identifyUser, router],
  )

  const login = React.useCallback(
    async (payload: LoginPayload) => {
      const result = await loginRequest(payload)
      resetCableConsumer()
      identifyUser(result.user)
      trackEvent("user_logged_in")
      setUser(result.user)
      setHasCompany(result.hasCompany)
      setCompanies(result.companies)
      router.refresh()
      return result
    },
    [identifyUser, router],
  )

  const logout = React.useCallback(async () => {
    await logoutRequest()
    resetCableConsumer()
    resetAnalytics()
    identifiedUserId.current = null
    setUser(null)
    setHasCompany(false)
    setCompanies([])
    setCurrentCompanyId(null)
    router.refresh()
  }, [router])

  const switchCompany = React.useCallback(
    async (companyId: string, options?: { redirectTo?: string }) => {
      // Optimistically reflect the choice so the navbar updates instantly; the
      // cookie + router.refresh() then re-seed the server-resolved value.
      setCurrentCompanyId(companyId)
      await switchCompanyRequest(companyId)
      // Land on Inicio so we never sit on a route scoped to the previous
      // company -- unless the caller names one every company has.
      router.push(options?.redirectTo ?? "/")
      router.refresh()
    },
    [router],
  )

  const value = React.useMemo<AuthContextValue>(
    () => ({ user, hasCompany, companies, currentCompanyId, signup, login, logout, switchCompany }),
    [user, hasCompany, companies, currentCompanyId, signup, login, logout, switchCompany],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = React.useContext(AuthContext)
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return ctx
}
